/**
 *  @namespace account/authentication
 */
import { isEmpty, pick, tapCatch, tapThen, test } from '~/utils/lib/core/prelude'
import Console from '~/utils/lib/core/console'
import Request from '~/utils/infra/account/request'
import { MFAProvider } from '~/utils/infra/account/mfa-provider'
import { useStore_account_session } from '~/stores/account/session'

/**
* @typedef {object} AuthResult
* @property {string} at AccessToken
* @property {string} atExp AccessToken expiration date (string ISO)
* @property {string} rt RefreshToken
* @property {string} rtExp RefreshToken expiration date (string ISO)
* @property {boolean} cookie
*/

/**
* @typedef {object} MfaChallengeResult
* @property {string} message
* @property {boolean} success
*/

/**
* @typedef {object} MfaResult
* @property {string} mfa MFA token
* @property {string} mfaExp MFA token expiration date (string ISO)
*/

export const FALLBACK_DISPATCH_URL = 'https://www.ghs.fr/'

/** @enum {typeof TRY_LOGIN_SUCCESS_TYPE[keyof typeof TRY_LOGIN_SUCCESS_TYPE]} */
export const TRY_LOGIN_SUCCESS_TYPE = /** @type {const} */ ({
  MFA_CHALLENGE: 'mfa challenge attended',
  COMPLETE: 'login complete',
})

export const REMOTE_ERRORS = {
  UNKNOWN_MFA_ERROR: 'MFA challenge unknown error'
}

const console = new Console('store:authentication')

export const useStore_account_authentication = defineStore('account/authentication', {
  actions: {
    /**
     * @function login_v2
     * @desc Login with eventual result prevented with a MFA challenge
     * @param {object} options
     * @param {string} options.email
     * @param {string} options.password
     * @param {boolean} options.rememberMe
     * @return {Promise<TRY_LOGIN_SUCCESS_TYPE>}
     */
    login_v2({ email, password, rememberMe }) {
      const data = {
        login: email,
        mdp: password,
        resterConnecte: rememberMe,
        atLong: false, // set a server-side life token of 15mn instead of 5
      }

      const request = new Request({
        method: 'post',
        route: 'v2/auth',
        data
      })

      const session = useStore_account_session()

      return request.exec()
        .then((res) => {

          switch (isEmpty(res.mfa)) {

            case false: {
              console.log('MFA challenge required')
              const /** @type {MfaResult} */ res_ = res
              session.$patch(
                {
                  mfaToken: res_.mfa
                }
              )
              return TRY_LOGIN_SUCCESS_TYPE.MFA_CHALLENGE
            }

            case true: {
              console.log('no MFA challenge required, proceeding to normal login')
              const /** @type {AuthResult} */ res_ = res
              return session.create(
                  {
                    accessToken: res_.at,
                    refreshToken: res_.rt,
                    isPersistent: rememberMe,
                  }
                )
                .then(() => TRY_LOGIN_SUCCESS_TYPE.COMPLETE)
            }

          }
        })
        // (defensive)
        // Destroy possibly newly cached session on any error catched,
        // as we DO NOT want a wrong cache leading to a later wrong restoration
        .catch(tapCatch(
          () => session.destroy(false))
        )
    },

    /**
     * @function resendCode
     * @param {object} options
     * @param {number} options.resendCount
     * @return {Promise<void>}
     */
    resendCode({ resendCount }) {

      const request = new Request({
        method: 'get',
        route: '/mfa/totp',
        params: {
          retry: resendCount > 1 // Changes the email provider when sent more then 1
        }
      })

      return request.accept(MFAProvider)
        .then(() => request.exec())
    },

    /**
     * @function submitCode
     * @param {object} options
     * @param {string} options.code
     * @param {boolean} options.rememberMe
     * @return {Promise<void>}
     * @throws {Error} Thrown if the code is invalid
     */
    submitCode({ code, rememberMe } ) {

      const request = new Request({
        method: 'post',
        route: '/mfa/totp/validate',
        data: {
          code,
        }
      })

      const session = useStore_account_session()

      return request.accept(MFAProvider)
        .then(() => request.exec())
        .then( /** @param {(AuthResult | {}) & MfaChallengeResult} res */ (res) => {
          switch (true) {

            case 'success' in res
              && res.success: {
              // @ts-ignore: unsafe COERCE
              const /** @type {AuthResult & MfaChallengeResult} */ res_ = res

              console.log('MFA challenge succeeded, logging user')
              return session.create(
                {
                  accessToken: res_.at,
                  refreshToken: res_.rt,
                  isPersistent: rememberMe
                },
              )
            }

            case 'success' in res
              && !res.success:
              console.warn('MFA challenge failed', res)
              throw new Error(res.message)

            default:
              console.warn('MFA challenge unknown error')
              throw new Error(REMOTE_ERRORS.UNKNOWN_MFA_ERROR)
          }
        })
    },

    /**
     * @function logout
     * @desc
     *    (!) Handshake logout via exec last request to micro services,
     *        thus still needs current session
     * @param {boolean} [hardDestroy=false]
     * @return {Promise<void>}
     */
    logout(hardDestroy = false) {
      const request = new Request({
        method: 'put',
        route: '/deconnexion'
      })

      const session = useStore_account_session()

      return request.authorize().exec()
        .then(() => session.destroy(hardDestroy))
        .then(() => console.log(`user has been logged out`))
    },
    /**
     * @function restore
     * @param {boolean} [updateExpiration=false]
     * @return {Promise<void>}
     */
    restore(updateExpiration = false) {
      const session = useStore_account_session()

      return session.retrieve()
        .then(tapThen(
          cache => console.log('found stored session', cache))
        )
        .then(cache => {

          const promises = []

          // re-hydrate the session dynamic cache
          const refreshData = pick(
            [ 'refreshToken', 'accessToken' ],
            cache
          )

          session.$patch(refreshData)

          // a validated session retrieved will always be persistent session
          const isPersistent = true

          if (updateExpiration)
            promises.push(
              session.updateExpiration(isPersistent)
            )

          return Promise.all(promises)
        })
        .then(() => console.log(`user session has been restored`))
    },
    /**
     * @function refresh
     * @return {Promise<void>}
     */
    refresh() {
      const session = useStore_account_session()
      const refreshToken = session.refreshToken
      const account_api = import.meta.env.VITE_ACCOUNT_API_URL

      const withPreprodSuffix = test(/preprod/, account_api)

      // @XXX Access Token "preprod" suffix should not be managed client side
      const rt_key = withPreprodSuffix ? 'rt-preprod' : 'rt'

      const headers = {
        [rt_key]: refreshToken,
      }

      const request = new Request({
        method: 'put',
        route: 'refresh',
      })

      return request.setHeaders(headers).exec()
        .then(
          /** @param {AuthResult} res */
          ({ rt, at }) => session.refresh(
            {
              refreshToken: rt,
              accessToken: at
            },
          )
        )
        .then(() => console.log('token refreshed'))
    },

    /**
     * @function refresh
     * @param {object} options
     * @param {string} options.email
     * @param {string} [options.url]
     * @return {Promise<any>}
     */
    resetPassword({ email, url }) {

      const request = new Request({
        method: 'put',
        route: 'reset-mdp/demander',
        data: {
          hamac: 'true', // necessary to guarantee the right email template
          login: email,
          url, // if login is made from dispatch page, keep the url
          optionRecupId: '0' // @NOTE Should be 0 for email password reset
        }
      })

      return request.exec()
    },

    /**
     * @function changePassword
     * @param {object} options
     * @param {string} options.password
     * @param {string} options.token
     * @return {Promise<{ status: boolean, url: string | undefined }>}
    */
    changePassword({ password, token }) {

      const request = new Request({
        method: 'put',
        route: 'changer-mdp/confirmer',
        data: {
          mdp: password,
          mdpToken: token,
        }
      })

      return request.exec()
    },
  }
})
